import React, { useEffect, useState } from 'react';
import { Cpu, Loader2 } from 'lucide-react';

import { type ReglagesModeles, memoireService } from '../../services/api/memoire';

interface Props {
  estAdmin: boolean;
  notifier: { showError: (t: string, m: string) => void; showInfo: (t: string, m: string) => void };
}

const selecteur = 'rounded-full border border-eg-bord bg-eg-blanc py-1.5 pl-3 pr-8 text-[12.5px] text-eg-encre focus:border-eg-accent focus:outline-none focus:ring-4 focus:ring-eg-accent/10 disabled:opacity-60';

/**
 * Quel modèle sert à quoi, et le choix de chacun.
 *
 * Chaque usage de la plateforme a son Claude ; un assistant peut imposer le
 * sien. Les moteurs qui ne sont pas Claude (transcription, voix, vidéo,
 * images) sont indiqués pour information.
 */
const Modeles: React.FC<Props> = ({ estAdmin, notifier }) => {
  const [reglages, setReglages] = useState<ReglagesModeles | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);

  useEffect(() => {
    memoireService.modeles().then(setReglages).catch(() => notifier.showError('Chargement impossible', 'Les réglages des modèles n\'ont pas pu être lus.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const regler = async (cle: string, champs: { usage?: string; assistant?: number; modele: string }) => {
    setEnCours(cle);
    try {
      setReglages(await memoireService.reglerModele(champs));
    } catch (e: any) {
      notifier.showError('Réglage impossible', e.message);
    } finally {
      setEnCours(null);
    }
  };

  if (!reglages) {
    return <div className="flex justify-center py-12 text-eg-gris"><Loader2 className="h-4 w-4 animate-spin" /></div>;
  }

  const nom = (id: string) => reglages.catalogue.find((m) => m.id === id)?.nom || id;
  const options = (avecDefaut: boolean) => (
    <>
      {avecDefaut && <option value="">Réglage de la rédaction</option>}
      {reglages.catalogue.map((m) => <option key={m.id} value={m.id}>{m.nom}</option>)}
    </>
  );
  const redaction = reglages.usages.find((u) => u.cle === 'redaction')?.modele || reglages.defaut;

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-2xl bg-eg-blanc shadow-carte outline outline-1 -outline-offset-1 outline-black/[0.07]">
        <div className="border-b border-eg-bordure px-5 py-4">
          <h3 className="text-[14px] font-medium text-eg-encre">Ce que fait Claude sur la plateforme</h3>
          <p className="mt-0.5 text-[12.5px] text-eg-gris">
            Un modèle par usage. Le changement vaut pour les demandes suivantes, sans redéploiement.
          </p>
        </div>
        {reglages.usages.map((usage, index) => (
          <div key={usage.cle} className={`flex flex-wrap items-center gap-3 px-5 py-3.5 ${index > 0 ? 'border-t border-eg-bordure' : ''}`}>
            <div className="min-w-[220px] flex-1">
              <div className="text-[13.5px] font-medium text-eg-encre">{usage.libelle}</div>
              <div className="text-[12px] text-eg-gris">{usage.description}</div>
            </div>
            {enCours === usage.cle && <Loader2 className="h-3.5 w-3.5 animate-spin text-eg-accent" />}
            <select
              value={usage.modele}
              disabled={!estAdmin || enCours !== null}
              onChange={(e) => regler(usage.cle, { usage: usage.cle, modele: e.target.value })}
              className={selecteur}
            >
              {!reglages.catalogue.some((m) => m.id === usage.modele) && <option value={usage.modele}>{usage.modele}</option>}
              {options(false)}
            </select>
          </div>
        ))}
      </section>

      <section className="overflow-hidden rounded-2xl bg-eg-blanc shadow-carte outline outline-1 -outline-offset-1 outline-black/[0.07]">
        <div className="border-b border-eg-bordure px-5 py-4">
          <h3 className="text-[14px] font-medium text-eg-encre">Modèle propre à un assistant</h3>
          <p className="mt-0.5 text-[12.5px] text-eg-gris">
            Par défaut, chaque assistant suit le réglage de la rédaction ({nom(redaction)}). Un assistant
            exigeant peut monter d'un cran, un assistant de routine descendre.
          </p>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {reglages.assistants.map((assistant, index) => (
            <div key={assistant.id} className={`flex items-center gap-3 px-5 py-2.5 ${index > 0 ? 'border-t border-eg-bordure' : ''}`}>
              <span className="min-w-0 flex-1 truncate text-[13px] text-eg-brun">{assistant.titre}</span>
              {enCours === `a${assistant.id}` && <Loader2 className="h-3.5 w-3.5 animate-spin text-eg-accent" />}
              <select
                value={assistant.modele}
                disabled={!estAdmin || enCours !== null}
                onChange={(e) => regler(`a${assistant.id}`, { assistant: assistant.id, modele: e.target.value })}
                className={selecteur}
              >
                {assistant.modele && !reglages.catalogue.some((m) => m.id === assistant.modele) && (
                  <option value={assistant.modele}>{assistant.modele}</option>
                )}
                {options(true)}
              </select>
            </div>
          ))}
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2">
        <section className="rounded-2xl bg-eg-blanc p-5 shadow-carte outline outline-1 -outline-offset-1 outline-black/[0.07]">
          <h3 className="text-[14px] font-medium text-eg-encre">Les Claude disponibles</h3>
          <ul className="mt-3 space-y-2.5">
            {reglages.catalogue.map((m) => (
              <li key={m.id} className="text-[12.5px]">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium text-eg-encre">{m.nom}</span>
                  <span className="ml-auto shrink-0 font-mono text-[11px] text-eg-estompe">
                    {m.prix_entree} $ / {m.prix_sortie} $ par M de jetons
                  </span>
                </div>
                <div className="text-eg-gris">{m.note}</div>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11.5px] text-eg-estompe">Prix publics Anthropic : lecture / écriture, par million de jetons.</p>
        </section>
        <section className="rounded-2xl bg-eg-blanc p-5 shadow-carte outline outline-1 -outline-offset-1 outline-black/[0.07]">
          <h3 className="text-[14px] font-medium text-eg-encre">Les autres moteurs</h3>
          <ul className="mt-3 space-y-2.5">
            {reglages.autres.map((a) => (
              <li key={a.usage} className="flex gap-2.5 text-[12.5px]">
                <Cpu className="mt-0.5 h-3.5 w-3.5 shrink-0 text-eg-estompe" />
                <div>
                  <div className="text-eg-encre"><span className="font-medium">{a.usage}</span> · {a.moteur}</div>
                  {a.note && <div className="text-eg-gris">{a.note}</div>}
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11.5px] text-eg-estompe">Réglés par l'équipe OmnIA sur le serveur.</p>
        </section>
      </div>
    </div>
  );
};

export default Modeles;
